import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useStable } from "../context/StableContext";
import { useAuth } from "../context/AuthContext";
import api, { errText } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Plus, ArrowRight, LogOut, Loader2, LogIn } from "lucide-react";
import { toast } from "sonner";

const ROLE_LABEL = { admin: "Gestore", manager: "Gestore", staff: "Staff", owner: "Proprietario cavallo", readonly: "Lettura" };

export default function StableSelect() {
  const { stables, select, refresh, loading } = useStable();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", address: "", contact: "", notes: "" });
  const [saving, setSaving] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [join, setJoin] = useState({ stable_name: "", desired_role: "staff" });
  const [joining, setJoining] = useState(false);

  const canCreate = !loading && stables.length === 0;

  const submitJoin = async (e) => {
    e.preventDefault();
    setJoining(true);
    try {
      const { data } = await api.post("/join-requests", join);
      toast.success(`Richiesta di adesione inviata a "${data.stable_name}". In attesa di approvazione.`);
      setJoinOpen(false);
      setJoin({ stable_name: "", desired_role: "staff" });
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    } finally {
      setJoining(false);
    }
  };

  const choose = (s) => {
    select(s);
    navigate("/");
  };

  const create = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await api.post("/stables", form);
      await refresh();
      toast.success("Scuderia creata");
      setOpen(false);
      choose(data);
    } catch (err) {
      toast.error(errText(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF8F5] px-4 py-10 sm:py-16">
      <div className="max-w-3xl mx-auto eq-fade-up">
        <div className="flex items-center justify-between mb-10">
          <div className="flex items-center gap-2">
            <div className="h-11 w-11 rounded-xl bg-[#1B3B2B] flex items-center justify-center">
              <span className="text-[#FAF8F5] font-display font-extrabold text-xl">E</span>
            </div>
            <span className="font-display font-extrabold text-2xl text-[#1B3B2B]">Equiro</span>
          </div>
          <Button variant="ghost" onClick={logout} data-testid="logout-btn" className="text-[#5F6B64]">
            <LogOut className="h-4 w-4 mr-2" /> Esci
          </Button>
        </div>

        <h1 className="font-display text-3xl font-bold text-[#1B3B2B]">Le tue scuderie</h1>
        <p className="text-[#5F6B64] mt-1 mb-8">Seleziona una scuderia per continuare o creane una nuova.</p>

        <div className="grid sm:grid-cols-2 gap-4">
          {stables.map((s) => (
            <button
              key={s.id}
              data-testid={`select-stable-${s.id}`}
              onClick={() => choose(s)}
              className="group text-left bg-white rounded-xl border border-[#E5DEC9] p-5 hover:border-[#8C532B] hover:shadow-md transition-all"
            >
              <div className="flex items-start justify-between">
                <div>
                  <h3 className="font-display font-bold text-lg text-[#1B3B2B]">{s.name}</h3>
                  <p className="text-sm text-[#5F6B64] mt-0.5">{s.address || "—"}</p>
                  <span className="inline-block mt-3 font-mono-label text-[10px] rounded-full px-2 py-0.5 bg-[#F0EAE1] text-[#8C532B]">
                    {ROLE_LABEL[s.role]}
                  </span>
                </div>
                <ArrowRight className="h-5 w-5 text-[#8C532B] opacity-0 group-hover:opacity-100 transition-opacity" />
              </div>
            </button>
          ))}

          <Dialog open={open} onOpenChange={setOpen}>
            {canCreate && (
            <DialogTrigger asChild>
              <button
                data-testid="new-stable-btn"
                className="flex flex-col items-center justify-center gap-2 bg-white rounded-xl border border-dashed border-[#C9BDA0] p-5 text-[#8C532B] hover:border-[#8C532B] hover:bg-[#FCFAF6] transition-all min-h-[140px]"
              >
                <Plus className="h-6 w-6" />
                <span className="font-semibold">Nuova scuderia</span>
              </button>
            </DialogTrigger>
            )}
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-display">Nuova scuderia</DialogTitle>
              </DialogHeader>
              <form onSubmit={create} className="space-y-4">
                <div>
                  <Label>Nome *</Label>
                  <Input required data-testid="stable-name-input" value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label>Indirizzo</Label>
                  <Input data-testid="stable-address-input" value={form.address}
                    onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label>Contatti</Label>
                  <Input data-testid="stable-contact-input" value={form.contact}
                    onChange={(e) => setForm({ ...form, contact: e.target.value })} className="mt-1.5" />
                </div>
                <div>
                  <Label>Note</Label>
                  <Textarea data-testid="stable-notes-input" value={form.notes}
                    onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" />
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={saving} data-testid="stable-save-btn"
                    className="bg-[#1B3B2B] hover:bg-[#142E1F]">
                    {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Crea scuderia
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={joinOpen} onOpenChange={setJoinOpen}>
            <DialogTrigger asChild>
              <button
                data-testid="join-stable-btn"
                className="flex flex-col items-center justify-center gap-2 bg-white rounded-xl border border-dashed border-[#C9BDA0] p-5 text-[#1B3B2B] hover:border-[#1B3B2B] hover:bg-[#FCFAF6] transition-all min-h-[140px]"
              >
                <LogIn className="h-6 w-6" />
                <span className="font-semibold">Voglio unirmi a una scuderia</span>
              </button>
            </DialogTrigger>
            <DialogContent data-testid="join-stable-dialog">
              <DialogHeader>
                <DialogTitle className="font-display">Unisciti a una scuderia</DialogTitle>
              </DialogHeader>
              <form onSubmit={submitJoin} className="space-y-4">
                <div>
                  <Label>Nome della scuderia *</Label>
                  <Input required data-testid="join-stable-name" value={join.stable_name}
                    onChange={(e) => setJoin({ ...join, stable_name: e.target.value })} className="mt-1.5"
                    placeholder="Nome esatto della scuderia" />
                </div>
                <div>
                  <Label>Tipo di rapporto desiderato</Label>
                  <Select value={join.desired_role} onValueChange={(v) => setJoin({ ...join, desired_role: v })}>
                    <SelectTrigger className="mt-1.5" data-testid="join-desired-role"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="staff">Staff</SelectItem>
                      <SelectItem value="owner">Proprietario cavallo</SelectItem>
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-[#5F6B64] mt-1.5">Il ruolo effettivo sarà assegnato dal Gestore in fase di approvazione.</p>
                </div>
                <DialogFooter>
                  <Button type="submit" disabled={joining} data-testid="join-submit-btn"
                    className="bg-[#1B3B2B] hover:bg-[#142E1F]">
                    {joining && <Loader2 className="h-4 w-4 mr-2 animate-spin" />} Invia richiesta
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
}
